import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

const MACROS_ORDER_KEY = 'macros_display_order';

/**
 * Macros in the order the agent arranged them in the sidebar, so every surface
 * that lists them stays in sync. Writing back saves a new order.
 */
export function useOrderedMacros() {
  const { uiSettings, updateUISettings } = useUISettings();
  const macros = useMapGetter('macros/getMacros');
  const isAssignedOnly = useMapGetter('isAssignedOnly');
  const canResolve = useMapGetter('canResolveConversations');

  const orderedMacros = computed({
    get: () => {
      const savedOrder = uiSettings.value?.[MACROS_ORDER_KEY] ?? [];
      if (isAssignedOnly.value) return [];
      const currentMacros = (macros.value ?? []).filter(
        macro =>
          canResolve.value ||
          !(macro.actions || []).some(
            action =>
              action.action_name === 'resolve_conversation' ||
              (action.action_name === 'change_status' &&
                ['resolved', '1'].includes(String(action.action_params?.[0])))
          )
      );

      if (!savedOrder.length || !currentMacros.length) {
        return currentMacros;
      }

      const orderMap = new Map(savedOrder.map((id, index) => [id, index]));

      // Anything the agent never arranged keeps its store order, at the end
      return [...currentMacros].sort(
        (a, b) =>
          (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity)
      );
    },
    set: newOrder => {
      updateUISettings({
        [MACROS_ORDER_KEY]: newOrder.map(({ id }) => id),
      });
    },
  });

  return { orderedMacros };
}
